/**
 * Driver Records — Shift & Mileage History + Driver Expenses (1.0.2)
 *
 * Dispatcher/admin-only read access to the accountability records the
 * driver app now captures:
 *   - Shift & Mileage History: starting/ending odometer readings + photos,
 *     total miles, open/closed status.
 *   - Driver Expenses: category, amount, receipt photo (or explicit
 *     "no receipt" exception + explanation), review status.
 *
 * These are intentionally separate from pickup/delivery proof (task-list's
 * ProofSection) — different accountability records, different endpoints,
 * never mixed.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  MenuItem, Select, Snackbar, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DashboardLayout from "../../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../examples/Navbars/DashboardNavbar";
import MDBox from "../../../components/MDBox";
import { getCurrentUser, getCurrentRole } from "../../../data/users";
import {
  listShiftHistory,
  getShiftOdometerPhoto,
  listDriverExpenses,
  getExpenseReceipt,
  reviewDriverExpense,
} from "../../../api/client";

const EXPENSE_CATEGORIES = ["Fuel", "Parking", "Toll", "Supplies", "Vehicle", "Other"];

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function money(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { style: "currency", currency: "USD" }) : "—";
}

function downloadDataUri(dataUri, filename) {
  const a = document.createElement("a");
  a.href = dataUri;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function toDataUri(base64) {
  if (!base64) return null;
  if (base64.startsWith("data:")) return base64;
  const mime = base64.startsWith("/9j") ? "image/jpeg" : base64.startsWith("iVBO") ? "image/png" : "image/jpeg";
  return `data:${mime};base64,${base64}`;
}

function exportRecordsCsv(rows, headers, keys, filename) {
  const safe = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) => keys.map((k) => safe(r[k])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

// ─── Photo viewer dialog (shared by odometer + receipt photos) ─────────────

function PhotoDialog({ open, title, loading, error, dataUri, downloadName, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {title}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="info">{error}</Alert>
        ) : dataUri ? (
          <Box display="flex" justifyContent="center">
            <img src={dataUri} alt={title} style={{ maxWidth: "100%", maxHeight: 480, borderRadius: 8 }} />
          </Box>
        ) : null}
      </DialogContent>
      {dataUri && !loading && (
        <DialogActions>
          <Button startIcon={<DownloadIcon />} onClick={() => downloadDataUri(dataUri, downloadName)}>
            Download
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

// ─── Shift & Mileage History tab ────────────────────────────────────────────

function ShiftHistoryTab({ notify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverFilter, setDriverFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [photoDialog, setPhotoDialog] = useState(null); // { title, loading, error, dataUri, downloadName }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listShiftHistory({
        driver: driverFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      if (res.ok) {
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
      } else {
        notify("error", "Could not load shift history.");
      }
    } catch (err) {
      notify("error", `Error loading shift history: ${err.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const viewPhoto = async (shift, stage) => {
    const has = stage === "start" ? shift.hasStartOdometerPhoto : shift.hasEndOdometerPhoto;
    const title = `${shift.driverName} — ${stage === "start" ? "Starting" : "Ending"} Odometer (${shift.shiftDate})`;
    if (!has) {
      setPhotoDialog({ title, loading: false, error: "Photo unavailable — legacy record where photo evidence does not exist.", dataUri: null });
      return;
    }
    setPhotoDialog({ title, loading: true, error: null, dataUri: null });
    try {
      const res = await getShiftOdometerPhoto(shift.id, stage);
      if (res.ok) {
        const data = await res.json();
        setPhotoDialog({
          title,
          loading: false,
          error: null,
          dataUri: toDataUri(data.photoData),
          downloadName: `odometer-${stage}-${shift.driverName.replace(/\s+/g, "_")}-${shift.shiftDate}.jpg`,
        });
      } else if (res.status === 404) {
        setPhotoDialog({ title, loading: false, error: "Photo unavailable — legacy record where photo evidence does not exist.", dataUri: null });
      } else {
        setPhotoDialog({ title, loading: false, error: "Could not load photo.", dataUri: null });
      }
    } catch (err) {
      setPhotoDialog({ title, loading: false, error: `Error loading photo: ${err.message}`, dataUri: null });
    }
  };

  const handleExport = () => {
    exportRecordsCsv(
      rows,
      ["Driver", "Shift Date", "Start Time", "Starting Odometer", "End Time", "Ending Odometer", "Total Miles", "Status"],
      ["driverName", "shiftDate", "startTime", "startOdometer", "endTime", "endOdometer", "totalMiles", "status"],
      `shift_history_${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignItems="center" mb={1}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Driver name" size="small" fullWidth value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="outlined" fullWidth onClick={handleExport} disabled={rows.length === 0}>
              Export CSV
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">No shifts found for the current filters.</Alert>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "2px solid #E5E7EB" }}>
                  {["Driver", "Date", "Start", "Start Odo", "Start Photo", "End", "End Odo", "End Photo", "Total Miles", "Status"].map((h) => (
                    <th key={h} style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "8px 10px" }}>{s.driverName}</td>
                    <td style={{ padding: "8px 10px" }}>{s.shiftDate}</td>
                    <td style={{ padding: "8px 10px" }}>{fmtDateTime(s.startTime)}</td>
                    <td style={{ padding: "8px 10px" }}>{s.startOdometer != null ? `${s.startOdometer.toLocaleString()} mi` : "—"}</td>
                    <td style={{ padding: "8px 10px" }}>
                      <Tooltip title={s.hasStartOdometerPhoto ? "View photo" : "No photo — legacy record"}>
                        <IconButton size="small" onClick={() => viewPhoto(s, "start")}>
                          <VisibilityIcon fontSize="small" color={s.hasStartOdometerPhoto ? "action" : "disabled"} />
                        </IconButton>
                      </Tooltip>
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {s.status === "open" ? <Chip label="Open Shift" size="small" color="warning" /> : fmtDateTime(s.endTime)}
                    </td>
                    <td style={{ padding: "8px 10px" }}>{s.endOdometer != null ? `${s.endOdometer.toLocaleString()} mi` : "—"}</td>
                    <td style={{ padding: "8px 10px" }}>
                      <Tooltip title={s.hasEndOdometerPhoto ? "View photo" : (s.status === "open" ? "Shift still open" : "No photo — legacy record")}>
                        <IconButton size="small" onClick={() => viewPhoto(s, "end")} disabled={s.status === "open"}>
                          <VisibilityIcon fontSize="small" color={s.hasEndOdometerPhoto ? "action" : "disabled"} />
                        </IconButton>
                      </Tooltip>
                    </td>
                    <td style={{ padding: "8px 10px" }}>{s.totalMiles != null ? `${s.totalMiles} mi` : "—"}</td>
                    <td style={{ padding: "8px 10px" }}>
                      <Chip label={s.status === "open" ? "Open" : "Closed"} size="small" color={s.status === "open" ? "warning" : "success"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </CardContent>

      {photoDialog && (
        <PhotoDialog
          open
          title={photoDialog.title}
          loading={photoDialog.loading}
          error={photoDialog.error}
          dataUri={photoDialog.dataUri}
          downloadName={photoDialog.downloadName}
          onClose={() => setPhotoDialog(null)}
        />
      )}
    </Card>
  );
}

// ─── Driver Expenses tab ────────────────────────────────────────────────────

function DriverExpensesTab({ notify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverFilter, setDriverFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [photoDialog, setPhotoDialog] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listDriverExpenses({
        driver: driverFilter || undefined,
        category: categoryFilter || undefined,
        reviewStatus: statusFilter || undefined,
      });
      if (res.ok) {
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
      } else {
        notify("error", "Could not load driver expenses.");
      }
    } catch (err) {
      notify("error", `Error loading driver expenses: ${err.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverFilter, categoryFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const viewReceipt = async (exp) => {
    const title = `${exp.driverName || exp.driverEmail} — ${exp.category} Receipt (${money(exp.amount)})`;
    if (exp.noReceiptFlag) {
      setPhotoDialog({ title, loading: false, error: `No receipt available. Driver explanation: "${exp.noReceiptExplanation || "—"}"`, dataUri: null });
      return;
    }
    if (!exp.hasReceipt) {
      setPhotoDialog({ title, loading: false, error: "No receipt photo on file for this expense.", dataUri: null });
      return;
    }
    setPhotoDialog({ title, loading: true, error: null, dataUri: null });
    try {
      const res = await getExpenseReceipt(exp.id);
      if (res.ok) {
        const data = await res.json();
        setPhotoDialog({
          title, loading: false, error: null,
          dataUri: toDataUri(data.photoData),
          downloadName: `receipt-${exp.category}-${exp.id.slice(0, 8)}.jpg`,
        });
      } else {
        setPhotoDialog({ title, loading: false, error: "Could not load receipt photo.", dataUri: null });
      }
    } catch (err) {
      setPhotoDialog({ title, loading: false, error: `Error loading receipt: ${err.message}`, dataUri: null });
    }
  };

  const decide = async (exp, decision) => {
    setBusyId(exp.id);
    try {
      const res = await reviewDriverExpense(exp.id, decision);
      if (res.ok) {
        notify("success", `Expense ${decision === "approved" ? "approved" : "rejected"}.`);
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        notify("error", data?.detail || "Could not update expense review status.");
      }
    } catch (err) {
      notify("error", `Error updating expense: ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleExport = () => {
    exportRecordsCsv(
      rows,
      ["Date", "Driver", "Category", "Merchant", "Amount", "Note", "Has Receipt", "No Receipt Reason", "Status"],
      ["createdAt", "driverName", "category", "merchant", "amount", "note", "hasReceipt", "noReceiptExplanation", "reviewStatus"],
      `driver_expenses_${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignItems="center" mb={1}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Driver name" size="small" fullWidth value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Select
              value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
              displayEmpty size="small" fullWidth
            >
              <MenuItem value="">All Categories</MenuItem>
              {EXPENSE_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Select
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty size="small" fullWidth
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="Submitted">Submitted</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="outlined" fullWidth onClick={handleExport} disabled={rows.length === 0}>
              Export CSV
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">No expenses found for the current filters.</Alert>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "2px solid #E5E7EB" }}>
                  {["Date", "Driver", "Category", "Merchant", "Amount", "Receipt", "Note", "Status", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((exp) => (
                  <tr key={exp.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{fmtDateTime(exp.createdAt)}</td>
                    <td style={{ padding: "8px 10px" }}>{exp.driverName || exp.driverEmail || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>{exp.category}</td>
                    <td style={{ padding: "8px 10px" }}>{exp.merchant || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>{money(exp.amount)}</td>
                    <td style={{ padding: "8px 10px" }}>
                      {exp.noReceiptFlag ? (
                        <Tooltip title={exp.noReceiptExplanation || "No explanation provided"}>
                          <Chip label="No Receipt" size="small" color="warning" onClick={() => viewReceipt(exp)} />
                        </Tooltip>
                      ) : (
                        <IconButton size="small" onClick={() => viewReceipt(exp)} disabled={!exp.hasReceipt}>
                          <VisibilityIcon fontSize="small" color={exp.hasReceipt ? "action" : "disabled"} />
                        </IconButton>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px", maxWidth: 180 }}>{exp.note || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>
                      <Chip
                        label={exp.reviewStatus || "Submitted"}
                        size="small"
                        color={exp.reviewStatus === "Approved" ? "success" : exp.reviewStatus === "Rejected" ? "error" : "default"}
                      />
                    </td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                      {(!exp.reviewStatus || exp.reviewStatus === "Submitted") && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton size="small" color="success" disabled={busyId === exp.id} onClick={() => decide(exp, "approved")}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" color="error" disabled={busyId === exp.id} onClick={() => decide(exp, "rejected")}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </CardContent>

      {photoDialog && (
        <PhotoDialog
          open
          title={photoDialog.title}
          loading={photoDialog.loading}
          error={photoDialog.error}
          dataUri={photoDialog.dataUri}
          downloadName={photoDialog.downloadName}
          onClose={() => setPhotoDialog(null)}
        />
      )}
    </Card>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────────

export default function DriverRecords() {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const userRole = useMemo(() => getCurrentRole(), []);
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState(null);

  const notify = (severity, message) => setSnackbar({ severity, message });

  const allowed = userRole === "admin";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Typography variant="h5" fontWeight="bold" mb={2}>
          Driver Records
        </Typography>

        {!currentUser ? (
          <Alert severity="error">Please sign in to access Driver Records.</Alert>
        ) : !allowed ? (
          <Alert severity="error">Access denied — Driver Records requires admin role.</Alert>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Shift & Mileage History" />
              <Tab label="Driver Expenses" />
            </Tabs>
            {tab === 0 ? <ShiftHistoryTab notify={notify} /> : <DriverExpensesTab notify={notify} />}
          </>
        )}
      </MDBox>

      {snackbar && (
        <Snackbar
          open={!!snackbar}
          autoHideDuration={5000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </DashboardLayout>
  );
}
